import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  Receipt,
  ShieldCheck,
} from 'lucide-react-native';
import { Platform } from 'react-native';
import { useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNotice } from '@/components/ConfirmDialog';
import { DatePickerSheet } from '@/components/DatePickerSheet';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { ItemCondition } from '@/db/types';
import { parseLocalDate, toLocalYMD } from '@/lib/dates';
import {
  COMMON_ROOMS,
  CONDITION_OPTIONS,
  INVENTORY_CATEGORIES,
  addInventoryItem,
} from '@/lib/inventory';
import { addDocument } from '@/lib/documents';
import { persistPickedFile } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import { scanReceiptBase64, type ParsedReceipt } from '@/lib/ocr';
import { pickAndScanFile } from '@/lib/scan-intake';
import { takeSmartScan } from '@/lib/smart-scan';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

export default function NewInventoryItem() {
  const router = useRouter();
  const db = useSQLiteContext();
  const notice = useNotice();

  const [name, setName] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [room, setRoom] = useState<string | null>(null);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [condition, setCondition] = useState<ItemCondition | null>(null);
  const [vendor, setVendor] = useState('');
  const [isInsured, setIsInsured] = useState(false);
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [receiptUri, setReceiptUri] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanHint, setScanHint] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [catOpen, setCatOpen] = useState(false);
  const [roomOpen, setRoomOpen] = useState(false);
  const [condOpen, setCondOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  // Smart Scan hand-off (/scan read an appliance label / product photo
  // and routed here) — consume once on mount, pre-fill, attach the
  // photo (persisted so it survives as the item's picture).
  useEffect(() => {
    const handoff = takeSmartScan('inventory');
    if (!handoff) return;
    (async () => {
      try {
        const ext = handoff.uri.match(/\.[a-zA-Z]+$/)?.[0] ?? '.jpg';
        const persisted = await persistPickedFile(
          handoff.uri,
          `inv_scan_${Date.now()}${ext}`
        );
        setPhoto(persisted);
      } catch (err) {
        console.warn('smart scan photo persist failed', err);
      }
      const parsed = handoff.parsed;
      if (parsed) {
        if (parsed.description?.trim()) setName(parsed.description.trim());
        else if (parsed.vendor?.trim()) setName(parsed.vendor.trim());
        if (parsed.vendor?.trim()) setBrand(parsed.vendor.trim());
        if (parsed.model) setModel(parsed.model);
        if (parsed.serial) setSerial(parsed.serial);
        setScanHint(
          'Smart scan filed this as an inventory item — check the details, then save.'
        );
      } else {
        setScanHint('Photo attached from Smart scan — fill in the details, then save.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickPhoto = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    const persisted = await persistPickedFile(
      a.uri,
      a.fileName ?? `item-${Date.now()}.jpg`
    );
    setPhoto(persisted);
  };

  /**
   * Capture a receipt photo, persist it (so it's saved as a Document on
   * save), AND run it through Google Vision to pre-fill vendor / purchase
   * date / price when the user has a key configured. Without a key we still
   * attach the photo so they don't lose the proof.
   */
  const scanReceipt = async () => {
    setScanHint(null);
    let res: ImagePicker.ImagePickerResult;
    try {
      if (Platform.OS !== 'web') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) return;
        res = await ImagePicker.launchCameraAsync({ quality: 0.7, base64: true });
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (!perm.granted) return;
        res = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          quality: 0.7,
          base64: true,
        });
      }
    } catch (err) {
      void notice({ title: 'Picker failed', message: String(err), tone: 'error' });
      return;
    }
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];

    // Persist the photo first so the user keeps proof even if OCR fails.
    const persisted = await persistPickedFile(
      a.uri,
      a.fileName ?? `receipt-${Date.now()}.jpg`
    );
    setReceiptUri(persisted);

    if (!a.base64) {
      setScanHint('Receipt attached — no image data for OCR, so fields are blank.');
      return;
    }

    setScanning(true);
    try {
      const result = await scanReceiptBase64(a.base64);
      if (!result.ok) {
        if (result.error.kind === 'no-key') {
          setScanHint(
            'Receipt attached. Add a Vision API key in Settings → Admin → API keys to auto-fill brand, date and price next time.'
          );
        } else {
          setScanHint(`Receipt attached, but OCR failed: ${result.error.message}`);
        }
        return;
      }
      applyParsedToItem(result.parsed);
    } finally {
      setScanning(false);
    }
  };

  /** Shared prefill for camera receipts and file uploads (Wave 54). */
  const applyParsedToItem = (parsed: ParsedReceipt) => {
    {
      const filled: string[] = [];
      if (parsed.vendor && !vendor) { setVendor(parsed.vendor); filled.push('vendor'); }
      if (parsed.model && !model) { setModel(parsed.model); filled.push('model'); }
      if (parsed.serial && !serial) { setSerial(parsed.serial); filled.push('serial'); }
      // Name: the user has to put SOMETHING here to save, so default to
      // the most plausible candidate — a real item line if the receipt
      // looks single-product, otherwise "Item from {vendor}" as a
      // placeholder they can rename.
      if (!name) {
        // OCR's anchored "Description"/"Item" detection is far more
        // reliable on invoices than our line-length heuristic — try it
        // first, then fall back.
        const guess = parsed.description ?? guessItemName(parsed.lines, parsed.vendor);
        if (guess) { setName(guess); filled.push('name'); }
      }
      if (parsed.amount_cents != null && !purchasePrice) {
        setPurchasePrice((parsed.amount_cents / 100).toFixed(2));
        filled.push('price');
      }
      if (parsed.date != null && !purchaseDate) {
        setPurchaseDate(toLocalYMD(parsed.date));
        filled.push('date');
      }
      setScanHint(
        filled.length > 0
          ? `Pre-filled: ${filled.join(', ')}. Edit anything off, then save.`
          : 'Scan complete — nothing obvious to pre-fill. Add the rest by hand.'
      );
    }
  };

  /** Any-file receipt intake (Wave 54): PDFs and images OCR + prefill;
   *  other documents attach as the receipt without a parse. */
  const scanReceiptFile = async () => {
    setScanHint(null);
    setScanning(true);
    try {
      const res = await pickAndScanFile();
      if (res.status === 'canceled') return;
      if (res.status === 'error') {
        void notice({ title: 'Picker failed', message: res.message, tone: 'error' });
        return;
      }
      const persisted = await persistPickedFile(res.uri, res.name);
      setReceiptUri(persisted);
      if (res.parsed) {
        applyParsedToItem(res.parsed);
      } else {
        setScanHint('Receipt file attached — fill in the details by hand.');
      }
    } finally {
      setScanning(false);
    }
  };

  const canSave = name.trim().length > 0;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      let receiptDocId: string | null = null;
      if (receiptUri) {
        receiptDocId = await addDocument(db, {
          propertyId: prop.id,
          kind: 'receipt',
          title: `Receipt — ${name.trim()}`,
          file_uri: receiptUri,
          mime_type: 'image/jpeg',
          vendor: vendor.trim() || null,
          cost_cents: parseCents(purchasePrice),
        });
      }
      await addInventoryItem(db, {
        propertyId: prop.id,
        name,
        category,
        room,
        brand: brand.trim() || null,
        model: model.trim() || null,
        serial_number: serial.trim() || null,
        purchase_date: parseDate(purchaseDate),
        purchase_cents: parseCents(purchasePrice),
        value_cents: parseCents(currentValue),
        condition,
        vendor: vendor.trim() || null,
        is_insured: isInsured ? 1 : 0,
        photo_uri: photo,
        receipt_doc_id: receiptDocId,
        notes: notes.trim() || null,
      });
      router.replace('/inventory' as never);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/inventory')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>New item</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 130 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Track an item</Text>
        <Text style={styles.sub}>
          Photo + receipt + serial = the difference between &quot;hope they pay out&quot;
          and a clean insurance claim. All optional.
        </Text>

        {/* Wave 54: compact add-photo row — the old 4:3 empty canvas
            dominated the screen before the user typed anything. The
            photo still expands to a full preview once one is chosen. */}
        <Pressable onPress={pickPhoto} style={photo ? styles.photoBtn : styles.photoBtnEmpty}>
          {photo ? (
            <Image source={{ uri: photo }} style={styles.photo} />
          ) : (
            <>
              <Camera size={20} color={Palette.forest} strokeWidth={1.8} />
              <View style={{ flex: 1 }}>
                <Text style={styles.photoLabel}>Add a photo</Text>
                <Text style={styles.photoSub}>Show the data plate if it has one.</Text>
              </View>
            </>
          )}
        </Pressable>

        <Pressable
          onPress={Platform.OS === 'web' ? scanReceiptFile : scanReceipt}
          disabled={scanning}
          style={({ pressed }) => [
            styles.receiptBtn,
            receiptUri && styles.receiptBtnDone,
            scanning && { opacity: 0.6 },
            pressed && { opacity: 0.9 },
          ]}>
          <Receipt size={18} color={receiptUri ? Palette.cream : Palette.honeyDeep} strokeWidth={1.8} />
          <View style={{ flex: 1 }}>
            <Text style={[styles.receiptText, receiptUri && { color: Palette.cream }]}>
              {scanning
                ? 'Reading receipt…'
                : receiptUri
                ? 'Receipt attached'
                : 'Scan a receipt'}
            </Text>
            <Text style={[styles.receiptSub, receiptUri && { color: 'rgba(251,246,234,0.85)' }]}>
              {scanning
                ? 'Vision is parsing the text — a couple of seconds.'
                : receiptUri
                ? "Saved to Documents. Re-scan to replace."
                : Platform.OS === 'web'
                ? 'Photo or PDF — pre-fills vendor, date and price via Vision OCR.'
                : 'Pre-fills vendor, date and price via Vision OCR.'}
            </Text>
            {Platform.OS !== 'web' && !scanning ? (
              <Pressable onPress={() => void scanReceiptFile()} hitSlop={8}>
                <Text style={styles.scanAltLink}>Or upload a file (PDF, image, doc)</Text>
              </Pressable>
            ) : null}
          </View>
        </Pressable>
        {scanHint ? (
          <View style={styles.scanHint}>
            <Text style={styles.scanHintText}>{scanHint}</Text>
          </View>
        ) : null}

        <Section title="BASICS">
          <Field label="Name" value={name} onChangeText={setName} placeholder="e.g. LG OLED TV" />
          <Pick label="Category" value={category} placeholder="Tap to pick" onPress={() => setCatOpen(true)} />
          <Pick label="Room" value={room} placeholder="Where is it?" onPress={() => setRoomOpen(true)} />
        </Section>

        <Section title="MAKE & MODEL">
          <Field label="Brand" value={brand} onChangeText={setBrand} placeholder="LG, Whirlpool, etc." />
          <Field label="Model #" value={model} onChangeText={setModel} placeholder="From the data plate" />
          <Field label="Serial #" value={serial} onChangeText={setSerial} placeholder="Underside or back" />
        </Section>

        <Section title="PURCHASE">
          <View style={{ marginTop: Spacing.three }}>
            <Text style={styles.fieldLabel}>PURCHASED</Text>
            <Pressable
              onPress={() => setDateOpen(true)}
              style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
              <Text style={[styles.pickText, !purchaseDate && styles.pickPlaceholder]}>
                {purchaseDate
                  ? new Date(purchaseDate + 'T12:00:00').toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'Tap to pick date'}
              </Text>
              <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
            </Pressable>
          </View>
          <Field label="Price paid" value={purchasePrice} onChangeText={setPurchasePrice} placeholder="1299.99" inputMode="decimal" />
          <Field label="Current value" value={currentValue} onChangeText={setCurrentValue} placeholder="950.00" inputMode="decimal" />
          <Field label="Where you got it" value={vendor} onChangeText={setVendor} placeholder="Best Buy, eBay, etc." />
        </Section>

        <Section title="CONDITION & COVERAGE">
          <Pick
            label="Condition"
            value={condition ? CONDITION_OPTIONS.find((c) => c.v === condition)?.label ?? null : null}
            placeholder="Tap to pick"
            onPress={() => setCondOpen(true)}
          />
          <Pressable onPress={() => setIsInsured(!isInsured)} style={styles.insuredRow}>
            <View style={[styles.insuredIcon, isInsured && { backgroundColor: Palette.forest }]}>
              <ShieldCheck
                size={16}
                color={isInsured ? Palette.cream : Palette.forest}
                strokeWidth={1.8}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.insuredLabel}>Insured under policy</Text>
              <Text style={styles.insuredSub}>
                {isInsured
                  ? 'Covered. Make sure your receipt is in Documents.'
                  : "Tap if this item's on your homeowner's policy."}
              </Text>
            </View>
            <View style={[styles.toggle, isInsured && styles.toggleOn]}>
              <View style={[styles.toggleThumb, isInsured && styles.toggleThumbOn]} />
            </View>
          </Pressable>
        </Section>

        <Section title="NOTES">
          <TextInput
            value={notes}
            onChangeText={setNotes}
            placeholder="Anything to remember — wall mount, software version, accessories included"
            placeholderTextColor={Palette.inkFaint}
            multiline
            style={[styles.fieldInput, webNoOutline, { height: 88, textAlignVertical: 'top' }]}
          />
        </Section>

      </ScrollView>

      {/* Sticky save bar — keeps the primary action one tap away on long
          forms instead of forcing the user to scroll past every section. */}
      <StickyBottomBar>
        <Pressable
          onPress={save}
          disabled={!canSave || saving}
          style={({ pressed }) => [
            styles.saveBtn,
            { flex: 1 },
            (!canSave || saving) && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{saving ? 'Saving…' : 'Save item'}</Text>
        </Pressable>
      </StickyBottomBar>

      <PickerModal
        open={catOpen}
        title="Category"
        options={INVENTORY_CATEGORIES.slice()}
        current={category}
        onPick={(v) => {
          setCategory(v);
          setCatOpen(false);
        }}
        onClose={() => setCatOpen(false)}
      />
      <PickerModal
        open={roomOpen}
        title="Room"
        options={COMMON_ROOMS.slice()}
        current={room}
        onPick={(v) => {
          setRoom(v);
          setRoomOpen(false);
        }}
        onClose={() => setRoomOpen(false)}
      />
      <PickerModal
        open={condOpen}
        title="Condition"
        options={CONDITION_OPTIONS.map((c) => c.label)}
        current={condition ? CONDITION_OPTIONS.find((c) => c.v === condition)?.label ?? null : null}
        onPick={(label) => {
          const found = CONDITION_OPTIONS.find((c) => c.label === label);
          setCondition(found?.v ?? null);
          setCondOpen(false);
        }}
        onClose={() => setCondOpen(false)}
      />

      <DatePickerSheet
        open={dateOpen}
        value={(() => { const t = parseLocalDate(purchaseDate); return isNaN(t) ? null : t; })()}
        onPick={(ms) => {
          setPurchaseDate(toLocalYMD(ms));
          setDateOpen(false);
        }}
        onClose={() => setDateOpen(false)}
        title="Purchase date"
      />
    </SafeAreaView>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  inputMode = 'text',
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        style={[styles.fieldInput, webNoOutline]}
      />
    </View>
  );
}

function Pick({
  label,
  value,
  placeholder,
  onPress,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  onPress: () => void;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
        <Text style={[styles.pickText, !value && styles.pickPlaceholder]}>
          {value ?? placeholder}
        </Text>
        <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>
    </View>
  );
}

function PickerModal({
  open,
  title,
  options,
  current,
  onPick,
  onClose,
}: {
  open: boolean;
  title: string;
  options: string[];
  current: string | null;
  onPick: (v: string) => void;
  onClose: () => void;
}) {
  return (
    <Modal animationType="fade" transparent visible={open} onRequestClose={onClose}>
      <Pressable style={styles.modalBackdrop} onPress={onClose}>
        <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.modalTitle}>{title}</Text>
          <ScrollView style={{ maxHeight: 380 }}>
            {options.map((o) => {
              const sel = current === o;
              return (
                <Pressable
                  key={o}
                  onPress={() => onPick(o)}
                  style={({ pressed }) => [
                    styles.modalRow,
                    pressed && { backgroundColor: '#EEF3EE' },
                    sel && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.modalRowText}>{o}</Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function parseCents(raw: string): number | null {
  const s = raw.replace(/[^0-9.]/g, '');
  if (!s) return null;
  const n = parseFloat(s);
  if (isNaN(n)) return null;
  return Math.round(n * 100);
}

/**
 * Pick a plausible item-name line from receipt OCR. Strategy: scan past
 * the vendor / address header, skip subtotal / total / payment lines,
 * and grab the longest remaining line. Falls back to "Item from {vendor}".
 */
// Header-noise blacklist mirrors the OCR lib's stricter rules: single
// words like "Year"/"Code"/"Request" are column labels, never item names.
const ITEM_HEADER_BLACKLIST = new Set([
  'request','year','month','day','date','time','status','type','code',
  'category','kind','reference','ref','total','subtotal','tax','amount',
  'price','qty','quantity','number','no','id','name','title','description',
  'item','product','details','service','notes','comments','memo','from',
  'to','vendor','customer','order','receipt','invoice','page','paid','due',
  'balance','change','approved','approval','auth','terminal','lane',
]);

function guessItemName(lines: string[], vendor: string | null): string | null {
  if (!lines || lines.length === 0) return vendor ? `Item from ${vendor}` : null;
  const headerSkip = vendor ? lines.findIndex((l) => l.includes(vendor)) + 1 : 3;
  const candidates: string[] = [];
  const skipPattern =
    /\b(total|subtotal|tax|change|tendered|cash|visa|mastercard|debit|credit|tip|gratuity|due|balance|receipt|store|cashier|register|date|time|tel|phone|fax|www|http|qty|item\s*#|sku|barcode|terminal|invoice|order\s*id|paid|seller|purchaser|customer|client|buyer|billed|ship\s*to|sold\s*to|description|quantity|amount|price|unit|po\s*#|po\s*number|email|url|request|status|type|category|kind|code|year|month|day|notes?|comments?)\b/i;
  const addressPattern =
    /^\d+\s+\S|\b(road|rd\.?|street|st\.?|avenue|ave\.?|blvd\.?|drive|dr\.?|lane|ln\.?|court|ct\.?|way|highway|hwy\.?|parkway|pkwy\.?|suite|ste\.?|apt\.?|unit|po\s*box)\b|,\s*[A-Z]{2}\s+\d{5}/i;
  for (let i = Math.max(0, headerSkip); i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.length < 4) continue;
    if (skipPattern.test(line)) continue;
    if (addressPattern.test(line)) continue;
    if (/^\d/.test(line)) continue;
    if (/^[\d\W]+$/.test(line)) continue;
    if (/\$\s?\d|^\d+\.\d{2}$/.test(line)) continue;
    if (/@|^https?:|^www\./i.test(line)) continue;
    // Single-word column headers — never pick.
    const norm = line.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (ITEM_HEADER_BLACKLIST.has(norm)) continue;
    // Must be multi-word OR ≥ 6 letters; weeds out stray "Year" / "Code".
    const words = line.split(/\s+/).filter(Boolean);
    const letters = line.replace(/[^a-z]/gi, '');
    if (words.length < 2 && letters.length < 6) continue;
    candidates.push(line);
  }
  if (candidates.length === 0) return vendor ? `Item from ${vendor}` : null;
  candidates.sort((a, b) => b.length - a.length);
  return candidates[0].replace(/\s+/g, ' ').slice(0, 80);
}

function parseDate(raw: string): number | null {
  const t = parseLocalDate(raw);
  if (isNaN(t)) return null;
  return t;
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  photoBtn: {
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
    aspectRatio: 4 / 3,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    overflow: 'hidden',
  },
  photoBtnEmpty: {
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  photo: { width: '100%', height: '100%' },
  photoEmpty: {
    flex: 1,
    alignItems: 'center', justifyContent: 'center',
    gap: 6, padding: 18,
  },
  photoLabel: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.inkSoft },
  scanAltLink: {
    marginTop: 6,
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.honeyDeep,
    textDecorationLine: 'underline',
  },
  photoSub: { fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkFaint },
  section: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  sectionTitle: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  pickPlaceholder: { color: Palette.inkFaint, fontFamily: Fonts.sansMedium },
  insuredRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: Spacing.three,
    padding: 12,
    backgroundColor: Palette.sand,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  insuredIcon: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  insuredLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  insuredSub: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 1, lineHeight: 16 },
  toggle: {
    width: 40, height: 24, borderRadius: 12,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleThumb: { width: 18, height: 18, borderRadius: 9, backgroundColor: Palette.cream },
  toggleThumbOn: { transform: [{ translateX: 16 }] },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink, marginBottom: 10 },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  receiptBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 14,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: Palette.honeyDeep,
    backgroundColor: Palette.honeySoft,
  },
  receiptBtnDone: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
    borderStyle: 'solid',
  },
  receiptText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: '#3A2A06',
  },
  receiptSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: '#5A4012',
    marginTop: 2,
    lineHeight: 16,
  },
  scanHint: {
    marginTop: 8, marginHorizontal: Spacing.four,
    padding: 12, borderRadius: 11,
    backgroundColor: 'rgba(225,163,61,0.18)',
    borderWidth: 1, borderColor: 'rgba(225,163,61,0.32)',
  },
  scanHintText: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: '#5A4012', lineHeight: 17,
  },
});
